import { useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  Input,
} from '@cartcloser/ui';
import { listAgents, sandboxTurn, type SandboxRecommendation } from '../lib/api.js';

export const Route = createFileRoute('/admin/$slug/sandbox')({
  validateSearch: (search: Record<string, unknown>): { agent?: string } => ({
    agent: typeof search.agent === 'string' ? search.agent : undefined,
  }),
  component: SandboxPage,
});

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  recommendations?: SandboxRecommendation[];
}

function SandboxPage() {
  const { slug } = Route.useParams();
  const { agent: agentId } = Route.useSearch();
  const scoped = !!agentId;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A scoped test pins an agent, so it's "routed" from the first turn;
  // otherwise the Receptionist must route before free typing is allowed.
  const [routed, setRouted] = useState(scoped);
  const [routedAgentId, setRoutedAgentId] = useState<string | undefined>(agentId);
  // The engine's current ranked shortlist — a live window into what the agent
  // would recommend given the chat so far. Holds the latest non-empty set.
  const [topPicks, setTopPicks] = useState<SandboxRecommendation[]>([]);
  // Must-haves the shopper has stated, with whether the specs can verify them.
  const [mustHaves, setMustHaves] = useState<{ text: string; inSpecs: boolean }[]>([]);
  const [considerationSetEmpty, setConsiderationSetEmpty] = useState(false);
  const [relaxationImpacts, setRelaxationImpacts] = useState<string[]>([]);
  const conversationId = useRef<string | undefined>(undefined);
  // Bumped on reset so an in-flight turn's response is discarded rather than
  // landing in a conversation the user has already cleared.
  const genRef = useRef(0);

  const agents = useQuery({ queryKey: ['agents', slug], queryFn: () => listAgents(slug) });
  const targetAgent = agentId ? agents.data?.find((a) => a.id === agentId) : undefined;

  // Categories = the product types the agents cover (their expertise). Shown
  // as the ONLY way to start when unrouted — you can't free-type to the
  // Receptionist. Once routed, chips become the agent's pain-point prompts.
  const categories = [
    ...new Set((agents.data ?? []).filter((a) => a.enabled).map((a) => a.expertise)),
  ];
  const routedAgent = agents.data?.find((a) => a.id === routedAgentId);
  const chipItems = routed ? (routedAgent?.suggestedPrompts ?? []) : categories;

  const last = messages[messages.length - 1];
  const lastHadRec = last?.role === 'assistant' && (last.recommendations?.length ?? 0) > 0;
  const showChips = chipItems.length > 0 && !busy && (!routed || !lastHadRec);

  async function sendText(text: string) {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    const gen = genRef.current; // if a reset bumps this, drop the response
    setError(null);
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: trimmed }]);
    setBusy(true);
    try {
      const res = await sandboxTurn(slug, trimmed, conversationId.current, agentId);
      if (genRef.current !== gen) return; // conversation was reset mid-flight
      conversationId.current = res.conversationId;
      if (!scoped && res.routed) {
        setRouted(true);
        setRoutedAgentId(res.agentId ?? undefined);
      }
      if (res.recommendations.length > 0) setTopPicks(res.recommendations);
      if (res.mustHaves.length > 0) setMustHaves(res.mustHaves);
      setConsiderationSetEmpty(res.considerationSetEmpty);
      setRelaxationImpacts(res.relaxationImpacts);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: res.reply,
          // Inline cards only when the agent actually recommends (not while qualifying).
          recommendations: res.recommended ? res.recommendations : undefined,
        },
      ]);
    } catch (err) {
      if (genRef.current !== gen) return;
      setError(err instanceof Error ? err.message : 'The agent failed to respond.');
    } finally {
      if (genRef.current === gen) setBusy(false);
    }
  }

  function send(e: React.FormEvent) {
    e.preventDefault();
    if (routed) void sendText(input);
  }

  function reset() {
    genRef.current += 1; // invalidate any in-flight turn
    conversationId.current = undefined;
    setMessages([]);
    setError(null);
    setBusy(false);
    setInput('');
    setRouted(scoped);
    setRoutedAgentId(agentId);
    setTopPicks([]);
    setMustHaves([]);
    setConsiderationSetEmpty(false);
    setRelaxationImpacts([]);
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sandbox</h1>
          <p className="text-sm text-muted-foreground">
            {agentId ? (
              <>
                Chatting directly with{' '}
                <span className="font-medium text-foreground">
                  {targetAgent?.expertise ?? 'a specific agent'}
                </span>{' '}
                — the Receptionist is skipped.
              </>
            ) : (
              'Chat with your live agents exactly as a shopper would. The Receptionist routes by expertise.'
            )}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={reset}>
          Reset
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="flex h-[60vh] flex-col lg:col-span-2">
          {routed && !scoped ? (
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2 text-xs text-muted-foreground">
              <span>
                Talking to{' '}
                <span className="font-medium text-foreground">
                  {routedAgent?.expertise ?? 'an expert'}
                </span>
              </span>
              <button
                type="button"
                onClick={reset}
                className="font-medium text-primary hover:underline"
              >
                ← Choose another product
              </button>
            </div>
          ) : null}
          <CardContent className="flex-1 space-y-4 overflow-y-auto p-4">
            {messages.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {routed
                  ? 'Tap a common question to start, or type your own.'
                  : 'What are you shopping for? Pick a product to begin.'}
              </p>
            ) : null}
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                <div
                  className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-secondary-foreground'
                  }`}
                >
                  {m.content}
                </div>
                {m.recommendations && m.recommendations.length > 0 ? (
                  <div className="mt-2 space-y-2">
                    {m.recommendations.map((r) => (
                      <div
                        key={r.productId}
                        className="rounded-md border border-border p-2 text-sm"
                      >
                        <div className="flex items-start gap-2">
                          <Badge variant="outline" className="mt-0.5 shrink-0">
                            #{r.position}
                          </Badge>
                          <div className="min-w-0">
                            <p className="font-medium leading-tight">
                              {r.title}
                              {r.priceCents != null ? (
                                <span className="ml-1 text-primary">
                                  ${(r.priceCents / 100).toFixed(2)}
                                </span>
                              ) : null}
                            </p>
                            {r.reason ? (
                              <p className="text-xs text-muted-foreground">{r.reason}</p>
                            ) : null}
                            {r.priceCents != null && r.priceUpdatedAt ? (
                              <p className="text-[10px] text-muted-foreground/70">
                                Price as at {new Date(r.priceUpdatedAt).toLocaleDateString()}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            {busy ? <p className="text-sm text-muted-foreground">Agent is thinking…</p> : null}
            {showChips ? (
              <div className="flex flex-wrap gap-2">
                {chipItems.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void sendText(s)}
                    disabled={busy}
                    className="rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:border-primary/40 hover:bg-accent disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            ) : null}
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </CardContent>
          <form onSubmit={send} className="flex gap-2 border-t border-border p-3">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={routed ? 'Ask about a product…' : 'Pick a product above to start'}
              disabled={busy || !routed}
            />
            <Button type="submit" loading={busy} disabled={!routed || !input.trim()}>
              Send
            </Button>
          </form>
        </Card>

        <div className="space-y-4 self-start lg:sticky lg:top-6">
          {mustHaves.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Must-have features</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {mustHaves.map((m) => (
                  <div key={m.text} className="flex items-start justify-between gap-2 text-sm">
                    <span className="min-w-0">{m.text}</span>
                    {m.inSpecs ? (
                      <Badge variant="success" className="shrink-0">
                        In specs
                      </Badge>
                    ) : (
                      <Badge variant="warning" className="shrink-0">
                        Not in specs
                      </Badge>
                    )}
                  </div>
                ))}
                {mustHaves.some((m) => !m.inSpecs) ? (
                  <p className="text-xs text-muted-foreground">
                    “Not in specs” means the catalogue data can’t verify this requirement — the
                    agent will say so rather than claim it.
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {/* Live window into the engine's current ranked shortlist. */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                {considerationSetEmpty ? 'Closest alternatives' : 'Top picks right now'}
                <HelpTip label="About the consideration set">
                  Hard constraints are treated as pass/fail. If none of your products satisfy them
                  all, the agent explains the smallest useful relaxation and asks the shopper which
                  constraint to loosen instead of quietly recommending a mismatch.
                </HelpTip>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {considerationSetEmpty && relaxationImpacts.length > 0 ? (
                <div className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs text-muted-foreground">
                  <p className="font-medium text-foreground">No exact match</p>
                  {relaxationImpacts.slice(0, 2).map((impact) => (
                    <p key={impact} className="mt-1">
                      {impact}.
                    </p>
                  ))}
                </div>
              ) : null}
              {topPicks.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  The agent’s ranked shortlist appears here and updates as the chat narrows down.
                </p>
              ) : (
                topPicks.map((r) => (
                  <div key={r.productId} className="rounded-md border border-border p-2 text-sm">
                    <div className="flex items-start gap-2">
                      <Badge variant="outline" className="mt-0.5 shrink-0">
                        #{r.position}
                      </Badge>
                      <div className="min-w-0">
                        <p className="font-medium leading-tight">{r.title}</p>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-mono">{r.sku}</span>
                          {r.priceCents != null ? ` · $${(r.priceCents / 100).toFixed(2)}` : ''}
                        </p>
                        {r.reason ? (
                          <p className="mt-1 text-xs text-muted-foreground">{r.reason}</p>
                        ) : null}
                        {r.priceCents != null && r.priceUpdatedAt ? (
                          <p className="mt-1 text-[10px] text-muted-foreground/70">
                            Price as at {new Date(r.priceUpdatedAt).toLocaleDateString()}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
